
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, ArrowLeft } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ErrorDisplay from '@/components/auth/ErrorDisplay';
import { supabase } from '@/integrations/supabase/client';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (loading) return;
    
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Use Supabase's password reset functionality
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) throw error;
      
      // Email sent successfully
      setEmailSent(true);
      toast.success('Password reset link sent to your email');
    } catch (err: any) {
      setError(err.message || 'An error occurred while sending the password reset link');
      toast.error('Failed to send password reset link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>Forgot Password | Career Guide Kerala</title>
      </Helmet>
      
      <Navbar />
      
      <main className="flex-grow flex items-center justify-center p-4 sm:p-6 md:p-8">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-soft p-8 animate-fade-in">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-brand-blue mb-2">Reset Your Password</h1>
            <p className="text-gray-600">
              {!emailSent 
                ? "Enter your email and we'll send you a reset link" 
                : "Check your inbox for a password reset link"}
            </p>
          </div>
          
          <ErrorDisplay error={error} />
          
          {!emailSent ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    disabled={loading}
                    aria-disabled={loading ? 'true' : 'false'}
                    required
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-brand-blue hover:bg-brand-blue/90"
                disabled={loading}
                aria-disabled={loading ? 'true' : 'false'}
              >
                {loading ? "Sending reset link..." : "Send Reset Link"}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-6">
              <div className="bg-green-50 text-green-700 p-4 rounded-lg mb-6">
                <p className="font-medium">Reset link sent!</p>
                <p className="text-sm mt-1">Check your email for instructions to reset your password.</p>
              </div>
              
              <Button 
                onClick={() => navigate('/login')}
                className="bg-brand-blue hover:bg-brand-blue/90"
              >
                Return to Login
              </Button>
            </div>
          )}
          
          <div className="mt-8 text-center">
            <Link to="/login" className="inline-flex items-center text-sm text-brand-lightBlue hover:underline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ForgotPassword;
